/**
 * COPYRIGHT NOTICE
 * This file is categorized as "Custom Source Code"
 * and is subject to the terms and conditions defined in the
 * "LICENSE.txt" file, which is part of this source code package.
 */
import { useEffect, useState, } from 'react';
import {
	Box,
	Column,
	Icon,
	IconButton,
	Row,
	Text,
} from 'native-base';
import {
	FontAwesome5,
} from '@expo/vector-icons';
import { useDeviceOrientation } from '@react-native-community/hooks';
import smartTruncate from 'smart-truncate';
import { useDimensions } from '@react-native-community/hooks';
import { useActionSheet } from '@expo/react-native-action-sheet';
import getIsMobile from '../OneHat/functions/getIsMobile';
import useOneHatData from '../OneHat/data/useOneHatData';
import withRedux from '../OneHat/data/hoc/withRedux';
import testProps from '../OneHat/functions/testProps';
import _ from 'lodash';

const TRUNCATE_LENGTH = 30,
	TRUNCATE_POSITION = 20;

function QuestionHeader(props) {
	const {
			currentDivision,
			currentQuestion,
			showQuestionsInDivisions,
			actions,
		} = props,
		isMobile = getIsMobile(),
		isLandscape = useDeviceOrientation().landscape,
		screen = useDimensions().screen,
		{ showActionSheetWithOptions } = useActionSheet(),
		[isReady, setIsReady] = useState(false),
		[divisions, Divisions] = useOneHatData('Divisions'),
		[questions, Questions] = useOneHatData('Questions'),
		showDivisionsActionSheet = () => {
			const options = _.map(Divisions.entities, (division) => {
				return smartTruncate(division.divisions__name, TRUNCATE_LENGTH, { position: TRUNCATE_POSITION, });
			});
			options.unshift('Cancel');
			const cancelButtonIndex = 0;
			showActionSheetWithOptions({
				options,
				cancelButtonIndex,
			}, (ix) => {
				if (ix === cancelButtonIndex) {
					return;
				}
				const truncatedDivision = options[ix];
				const division = Divisions.getFirstBy((division) => {
					return smartTruncate(division.divisions__name, TRUNCATE_LENGTH, { position: TRUNCATE_POSITION, }) === truncatedDivision
				});
				actions.setDivisionById(division.id);
			});
		},
		showQuestionsActionSheet = () => {
			let questions;
			if (showQuestionsInDivisions) {
				questions = currentDivision.getQuestions();
			} else {
				questions = Questions.entities;
			}
			const options = _.map(questions, (question) => {
					let truncated = smartTruncate(question.questions__display_as, TRUNCATE_LENGTH, { position: TRUNCATE_POSITION, });
					if (currentQuestion.id === question.id) {
						truncated = '>> ' + truncated + ' <<';
					}
					return truncated;
				});
			options.unshift('Cancel');
			const cancelButtonIndex = 0;
			showActionSheetWithOptions({
				options,
				cancelButtonIndex,
			}, (ix) => {
				if (ix === cancelButtonIndex) {
					return;
				}
				const truncatedQuestion = options[ix],
					question = Questions.getFirstBy((question) => {
						const truncated = smartTruncate(question.questions__display_as, TRUNCATE_LENGTH, { position: TRUNCATE_POSITION, }),
							re = new RegExp('(>> )?(' + truncated + ')( <<)?');
						return !!truncatedQuestion.match(re);
					});
				if (question) {
					// This won't find the current one, but that's ok, as it's already the currentQuestion
					actions.setQuestionById(question.id);
				}
			});
		};

	useEffect(() => {
		if (!Divisions || !Questions) {
			return;
		}
		
		if (Divisions.isDestroyed || Questions.isDestroyed) {
			return; // for auto-reload of Expo
		}
		
		setIsReady(true);

	}, [Divisions, Questions]);
	
	if (!isReady || !currentQuestion || !currentDivision) {
		return null;
	}

	const iconButtonContainerProps = {
			justifyContent: 'center',
			alignItems: 'center',
			w: 50,
		},
		iconButtonProps = {
			w: '100%',
			px: 3,
		},
		iconProps = {
			alignSelf: 'center',
			color: '#fff',
			size: isLandscape ? 'md' : 'lg',
			px: 2,
		},
		textContainerProps = {
			flex: 8,
			justifyContent: 'center',
			alignItems: 'center',
			p: isLandscape ? 0 : 2
		},
		textProps = {
			alignSelf: 'center',
			textAlign: 'center',
			color: '#fff',
			px: 3,
			fontSize: isLandscape ? 14 : (screen.width > 600 ? 27 : 17),
			lineHeight: isLandscape ? 18 : (screen.width > 600 ? 30 : 20),
		};

	return <Column w="100%">
				<Row bg="primary.900">
					{isMobile && <Box {...iconButtonContainerProps}>
									<IconButton
										{...testProps('showDivisionsBtn')}
										{...iconButtonProps}
										icon={<Icon as={FontAwesome5} {...iconProps} name="list" />}
										onPress={showDivisionsActionSheet}
									/>
								</Box>}
					<Box {...iconButtonContainerProps}>
						<IconButton
							{...testProps('prevDivisionBtn')}
							{...iconButtonProps}
							icon={<Icon as={FontAwesome5} {...iconProps} name="angle-left" />}
							onPress={() => {
								actions.setDivisionById(currentDivision.divisions__prev);
							}}
							isDisabled={!currentDivision.divisions__prev}
						/>
					</Box>
					<Box {...textContainerProps}>
						<Text {...textProps} fontWeight="bold" textTransform="uppercase" fontFamily="RobotoCondensed_400Regular">{currentDivision.divisions__name}</Text>
					</Box>
					<Box {...iconButtonContainerProps}>
						<IconButton
							{...testProps('nextDivisionBtn')}
							{...iconButtonProps}
							icon={<Icon as={FontAwesome5} {...iconProps} name="angle-right" />}
							onPress={() => {
								actions.setDivisionById(currentDivision.divisions__next);
							}}
							isDisabled={!currentDivision.divisions__next}
						/>
					</Box>
				</Row>

				<Row bg="primary.700">
					{isMobile && <Box {...iconButtonContainerProps}>
									<IconButton
										{...testProps('showQuestionsBtn')}
										{...iconButtonProps}
										icon={<Icon as={FontAwesome5} {...iconProps} name="list" />}
										onPress={showQuestionsActionSheet}
										isDisabled={showQuestionsInDivisions ? currentDivision.getQuestionCount() === 1 : false}
									/>
								</Box>}
					<Box {...iconButtonContainerProps}>
						<IconButton
							{...testProps('prevQuestionBtn')}
							{...iconButtonProps}
							icon={<Icon as={FontAwesome5} {...iconProps} name="angle-left" />}
							onPress={() => {
								actions.setQuestionById(currentQuestion.questions__prev);
							}}
							isDisabled={!currentQuestion.questions__prev}
						/>
					</Box>
					<Box {...textContainerProps}>
						<Text {...textProps}>{currentQuestion.questions__display_as}</Text>
					</Box>
					<Box {...iconButtonContainerProps}>
						<IconButton
							{...testProps('nextQuestionBtn')}
							{...iconButtonProps}
							icon={<Icon as={FontAwesome5} {...iconProps} name="angle-right" />}
							onPress={() => {
								actions.setQuestionById(currentQuestion.questions__next);
							}}
							isDisabled={!currentQuestion.questions__next}
						/>
					</Box>
				</Row>
			</Column>;
	
}

export default withRedux(QuestionHeader, [
	'currentDivision',
	'currentQuestion',
	'showQuestionsInDivisions',
]);